<template>
    <div
        :class="['avatar', size, shape]"
        :style="'width:' + customSize + 'px;' + 'height:' + customSize + 'px;'"
    >
        <img
            v-if="avatarSrc"
            :src="avatarSrc"
            alt=""
            draggable="false"
        >
        <img
            v-else
            src="src/images/default-avatar.png"
            alt=""
            draggable="false"
        />
        <div
            v-if="enableOverlay"
            class="overlay"
            :class="overlayColor"
        >
            <i v-if="overlayIcon" :class="`mdi mdi-${overlayIcon}`" />
        </div>
        <template v-if="timelogStatus">
            <div
                v-if="size"
                :class="[
                    timelogStatus,
                    {'x-tiny': size == 'tiny'},
                    {tiny: size == 'small'},
                    {regular: size == 'medium'},
                    {medium: size == 'large' || size == 'x-large'},
                    {large: size == 'xx-large' || size == 'xxx-large'},
                ]"
                class="status"
            />
            <div
                v-else-if="customSize"
                :class="[
                    timelogStatus,
                    {'x-tiny': customSize <= 16},
                    {tiny: customSize > 16 && customSize <= 28},
                    {regular: customSize > 28 && customSize <= 48},
                    {medium: customSize > 48 && customSize < 64},
                    {large: customSize >= 64},
                ]"
                class="status"
            />
        </template>
        <template v-else-if="displayStatus">
            <div
                v-if="size"
                :class="[
                    (isOnline ? 'online' : 'offline'),
                    {'x-tiny': size == 'tiny'},
                    {tiny: size == 'small'},
                    {regular: size == 'medium'},
                    {medium: size == 'large' || size == 'x-large'},
                    {large: size == 'xx-large' || size == 'xxx-large'},
                ]"
                class="status"
            />
            <div
                v-else-if="customSize"
                :class="[
                    (isOnline ? 'online' : 'offline'),
                    {'x-tiny': customSize <= 16},
                    {tiny: customSize > 16 && customSize <= 28},
                    {regular: customSize > 28 && customSize <= 48},
                    {medium: customSize > 48 && customSize < 64},
                    {large: customSize >= 64},
                ]"
                class="status"
            />
        </template>
    </div>
</template>

<script>
    export default {
        name: 'Avatar',

        props: {
            src: {
                type: String,
                required: false,
                default: () => ''
            },

            timelogStatus: {
                type: String,
                required: false,
            },

            enableOverlay: {
                type: String,
                required: false
            },

            overlayColor: {
                type: String,
                default: 'green',
                required: false
            },

            overlayIcon: {
                type: String,
                required: false
            },

            shape: {
                type: String,
                required: false,
            },

            customSize: {
                type: [String, Number],
                default: '32',
                required: false
            },

            size: {
                type: String,
                default: '',
                required: false
            },
            displayStatus: {
                type: Boolean,
                required: false,
            },
            isOnline: {
                type: Boolean,
                required: false,
            }
        },

        data() {
            return {
                avatarSrc: ''
            };
        },

        watch: {
            src: {
                handler(src) {
                    let value = src.split('http');
                    if (value.length > 2) {
                        this.avatarSrc = 'http' + value[2];
                    } else {
                        this.avatarSrc = src;
                    }

                    let testerImage = new Image();
                    testerImage.onerror = () => {
                        this.avatarSrc = '';
                    };
                    testerImage.src = this.avatarSrc;
                },
                immediate: true
            }
        }
    };
</script>

<style lang="scss" scoped>
    @import 'source/stylesheets/sass/abstract/bpo-variables.scss';

    .avatar {
        @include user-select(none);
        display: inline-block;
        position: relative;
        overflow: hidden;
        flex-shrink: 0;

        img {
            height: 100%;
            width: 100%;
            border-radius: 50%;
            object-fit: cover;
            background: $white;
        }

        &.tiny {
            width: 16px !important;
            height: 16px !important;
        }

        &.small {
            width: 24px !important;
            height: 24px !important;
        }

        &.medium {
            width: 40px !important;
            height: 40px !important;
        }

        &.large {
            width: 48px !important;
            height: 48px !important;
        }

        &.x-large {
            width: 56px !important;
            height: 56px !important;
        }

        &.xx-large {
            width: 64px !important;
            height: 64px !important;
        }

        &.xxx-large {
            width: 72px !important;
            height: 72px !important;
        }

        &.rounded img {
            border-radius: 4px;
        }

        &.square img {
            border-radius: 0;
        }
    }

    .overlay {
        position: absolute;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        border-radius: 50%;
        z-index: 1;

        &.green {
            background: rgba($success, .5);
        }

        &.blue {
            background: rgba($accent, .5);
        }

        &.red {
            background: rgba($error, .5);
        }

        &.yellow {
            background: rgba($warning, .5);
        }

        i {
            font-size: 24px;
            color: $white;
        }
    }

    .status {
        position: absolute;
        bottom: 0;
        right: 0;
        border-radius: 50%;
        z-index: 2;

        &.large {
            height: 20px;
            width: 20px;
            box-shadow: 0 0 0 3px $white;
        }

        &.medium {
            height: 16px;
            width: 16px;
            box-shadow: 0 0 0 2px $white;
        }

        &.regular {
            height: 12px;
            width: 12px;
            box-shadow: 0 0 0 1.5px $white;
        }

        &.tiny {
            height: 9px;
            width: 9px;
        }

        &.x-tiny {
            height: 6px;
            width: 6px;
        }

        &.web,
        &.online {
            background: $success;
        }

        &.mobile {
            background: $accent;
        }

        &.offline {
            background: $tertiary;
        }
    }
</style>
